import React, { useEffect, useState } from 'react';
import { get, put, del } from '../api';
import { getDDLCollections } from '../utils/storage';
import './Modal.css';
import './EquipmentDetailModal.css';

// Field shared by every equipment category — the only metadata field for
// non-VT categories until each gets its own bespoke schema.
const TYPE_OF_OPERATION_FIELD = {
  key: 'typeOfOperation',
  label: 'Equipment — Type of Operation',
  kind: 'select',
  dropdownId: 'equipment_typeOfOperation',
};

// Per-category metadata schema. Categories not listed here fall back to just
// the shared Type of Operation dropdown.
//
// `dropdownId` for select fields is the value the user assigns in the DDL
// Registration UI. The matching DDL collection's `assignedTo` must include
// the same id.
const CATEGORY_FIELDS = {
  'VT Equipment': [
    TYPE_OF_OPERATION_FIELD,
    { key: 'sensorSize',       label: 'Sensor size',           kind: 'text' },
    { key: 'photoResolution',  label: 'Photo resolution',      kind: 'text' },
    { key: 'videoResolution',  label: 'Video resolution',      kind: 'text' },
    { key: 'videoFps',         label: 'Frames per second (video)', kind: 'text' },
    { key: 'iso',              label: 'ISO', kind: 'iso',
      defaultValue: 'Native auto' },
    { key: 'photoFormat',      label: 'Photo format', kind: 'format' },
    { key: 'videoFormat',      label: 'Video format', kind: 'format' },
  ],
  'UT Equipment': [
    TYPE_OF_OPERATION_FIELD,
    // Tristate switch (yes / no / na). Smart default — see the modal's
    // load useEffect — picks `no` when a UT Device is linked upstream,
    // `yes` otherwise. The third state `na` is an explicit user
    // override marking the device fields as Not applicable regardless.
    { key: 'acceptsModule', label: 'Accepts module', kind: 'tristate' },
    // Display-only fields populated from the linked UT Device payload
    // (equipment.utDevice). Rendered as "Not applicable" when
    // acceptsModule is anything except 'no'.
    { key: '_utDeviceName',       label: 'UT Device',                 kind: 'utDeviceName' },
    { key: '_utDeviceExpiration', label: 'UT Device expiration date', kind: 'utDeviceExpiration' },
  ],
  // UTC Module entries always carry a linked UT Device (backend filters
  // out anything that isn't a Module main + UT Device payload). No
  // Accepts-module switch — the UT Device fields are always rendered.
  // The renderers detect the absence of an acceptsModule field on the
  // schema and skip the "Not applicable" branch.
  'UTC Modules': [
    TYPE_OF_OPERATION_FIELD,
    { key: '_utDeviceName',       label: 'UT Device',                 kind: 'utDeviceName' },
    { key: '_utDeviceExpiration', label: 'UT Device expiration date', kind: 'utDeviceExpiration' },
  ],
  // Probe Modules: same payload-link pattern as UTC Modules, but the
  // payload is a Probe — name only, no expiration tracked. The four
  // ultrasonic-setup fields below feed the per-probe cells in the UT
  // datasheet PDF (probe[n].angleFrequency / .rangeGain).
  //   numberWithSuffix → user types digits, suffix appended on blur.
  //   autoText         → free text; defaults to "Auto" if left blank.
  'Probe Modules': [
    TYPE_OF_OPERATION_FIELD,
    { key: '_probeName',   label: 'Probe',        kind: 'probeName' },
    { key: 'angle',        label: 'Angle',        kind: 'numberWithSuffix', suffix: '°',    defaultValue: '0°' },
    { key: 'frequency',    label: 'Frequency',    kind: 'numberWithSuffix', suffix: ' MHz', defaultValue: '5 MHz' },
    { key: 'primaryGain',  label: 'Primary gain', kind: 'autoText',         defaultValue: 'Auto' },
    { key: 'primaryRange', label: 'Range',        kind: 'autoText',         defaultValue: 'Auto' },
  ],
};

// ── Expiry badge helpers (same logic as EquipmentPage) ────────────────
function expiryClass(iso) {
  if (!iso) return 'ddl-expiry-none';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'ddl-expiry-none';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.ceil((d.getTime() - today.getTime()) / (24 * 3600 * 1000));
  if (diff < 0)   return 'ddl-expiry-expired';
  if (diff <= 30) return 'ddl-expiry-soon';
  return 'ddl-expiry-set';
}

function expiryIcon(cls) {
  if (cls === 'ddl-expiry-expired') return '⚠ ';
  if (cls === 'ddl-expiry-soon')    return '⏰ ';
  if (cls === 'ddl-expiry-set')     return '📅 ';
  return '';
}

function formatDateBadge(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Stable module-level default so categories not listed in CATEGORY_FIELDS
// (Stepwedge / Modules / Probes / MT) return the SAME array reference on
// every call. Without this, getFieldsForCategory returned a fresh
// [TYPE_OF_OPERATION_FIELD] literal each render — the modal's load
// useEffect tracks `fields` in its deps, so every user interaction
// triggered a refetch that clobbered the unsaved dropdown selection.
const DEFAULT_FIELDS = [TYPE_OF_OPERATION_FIELD];

function getFieldsForCategory(category) {
  return CATEGORY_FIELDS[category] || DEFAULT_FIELDS;
}

// "Not applicable" / "Not available" are accepted verbatim — they do not get
// uppercased or prefixed with a dot.
const FORMAT_PASSTHROUGH = new Set(['not applicable', 'not available']);

// Normalise a format value: uppercase, ensure leading dot. Empty stays empty.
// Passthrough values (Not applicable / Not available) are returned as-is, with
// the canonical Title Case spelling.
function normaliseFormat(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  const lower = v.toLowerCase();
  if (FORMAT_PASSTHROUGH.has(lower)) {
    // Title-case the canonical form: "not applicable" → "Not applicable".
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  const up = v.toUpperCase();
  return up.startsWith('.') ? up : `.${up}`;
}

// Resolve a DDL list by `assignedTo` id. Mirrors the helper used by other modals.
function getDDLLabels(ddlCollections, dropdownId) {
  const pinnedLower = ['not applicable', 'not available'];
  const col = ddlCollections.find((c) => {
    if (!c.assignedTo) return false;
    if (Array.isArray(c.assignedTo)) return c.assignedTo.includes(dropdownId);
    return c.assignedTo === dropdownId;
  });
  if (!col) return [];
  const items = col.items || [];
  const pinned   = items.filter((it) => pinnedLower.includes(it.label.trim().toLowerCase()));
  const unpinned = items.filter((it) => !pinnedLower.includes(it.label.trim().toLowerCase()));
  return [...pinned, ...unpinned].map((it) => it.label);
}

export default function EquipmentDetailModal({ equipment, category, onClose, onSaved, onDeleted }) {
  const fields = getFieldsForCategory(category);

  const [metadata, setMetadata]       = useState({});
  const [ddlCollections, setDdl]      = useState([]);
  const [loading, setLoading]         = useState(true);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [editingIso, setEditingIso]   = useState(false);

  // Load existing metadata + DDL collections in parallel.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meta, ddl] = await Promise.all([
          get(`/equipment/${encodeURIComponent(equipment.id)}/metadata?category=${encodeURIComponent(category)}`),
          getDDLCollections(),
        ]);
        if (cancelled) return;
        const initial = meta?.metadata || {};
        // Apply defaults for fields that have one and are missing/empty.
        if (fields) {
          for (const f of fields) {
            if (f.kind === 'iso' && !initial[f.key]) initial[f.key] = f.defaultValue;
            if (f.kind === 'tristate' && initial[f.key] === undefined) {
              // Smart default for "Accepts module" (tristate yes/no/na):
              //   no  → a UT Device is linked upstream → show device fields
              //   yes → no linked device → device fields show "Not applicable"
              // 'na' is reserved for explicit user override.
              // Legacy boolean values get migrated below.
              if (f.key === 'acceptsModule') {
                initial[f.key] = equipment.utDevice ? 'no' : 'yes';
              } else {
                initial[f.key] = 'yes';
              }
            }
            // Legacy migration: booleans → tristate strings.
            if (f.kind === 'tristate' && typeof initial[f.key] === 'boolean') {
              initial[f.key] = initial[f.key] ? 'yes' : 'no';
            }
          }
        }
        setMetadata(initial);
        setDdl(ddl || []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [equipment.id, fields]);

  function setField(key, value) {
    setMetadata((m) => ({ ...m, [key]: value }));
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    const cleaned = { ...metadata };
    if (fields) {
      for (const f of fields) {
        if (f.kind === 'format') cleaned[f.key] = normaliseFormat(cleaned[f.key]);
        if (f.kind === 'iso' && (!cleaned[f.key] || !cleaned[f.key].trim())) {
          cleaned[f.key] = f.defaultValue;
        }
        if (f.kind === 'numberWithSuffix') {
          const v = String(cleaned[f.key] ?? '').trim();
          if (!v) cleaned[f.key] = f.defaultValue;
          else if (!v.endsWith(f.suffix)) cleaned[f.key] = `${v}${f.suffix}`;
        }
        if (f.kind === 'autoText') {
          const v = String(cleaned[f.key] ?? '').trim();
          if (!v) cleaned[f.key] = f.defaultValue;
        }
        if (f.kind === 'utDeviceName' || f.kind === 'utDeviceExpiration' || f.kind === 'probeName') {
          delete cleaned[f.key];
        }
      }
    }
    try {
      await put(`/equipment/${encodeURIComponent(equipment.id)}/metadata`, {
        category,
        metadata: cleaned,
        last_known_name: equipment.name || null,
        last_known_manufacturer: equipment.manufacturer || null,
      });
      setMetadata(cleaned);
      if (typeof onSaved === 'function') onSaved(equipment.id, cleaned);
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    const ok = window.confirm(
      `Delete saved metadata for "${equipment.name || equipment.id}"? This cannot be undone.`,
    );
    if (!ok) return;
    setSaving(true);
    setError('');
    try {
      await del(`/equipment/${encodeURIComponent(equipment.id)}/metadata?category=${encodeURIComponent(category)}`);
      if (typeof onDeleted === 'function') onDeleted(equipment.id);
      onClose();
    } catch (e) {
      setError(e.message || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  }

  // Per-field rendering.
  function renderField(f) {
    const v = metadata[f.key] ?? '';

    // ── Tristate toggle (Accepts module: yes / no / na) ────────────
    if (f.kind === 'tristate') {
      // Legacy boolean values may slip through if the load-effect didn't
      // migrate yet (e.g. unsaved edits in flight). Coerce defensively.
      let cur = v;
      if (cur === true)  cur = 'yes';
      if (cur === false) cur = 'no';
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <div className="equipment-yesno-row">
            <button
              type="button"
              className={`btn btn-sm ${cur === 'yes' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setField(f.key, 'yes')}
            >Yes</button>
            <button
              type="button"
              className={`btn btn-sm ${cur === 'no' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setField(f.key, 'no')}
            >No</button>
            <button
              type="button"
              className={`btn btn-sm ${cur === 'na' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setField(f.key, 'na')}
            >Not applicable</button>
          </div>
        </div>
      );
    }

    // ── Linked UT Device — read-only display ───────────────────────
    // Schemas with an Accepts-module tristate (UT Equipment) gate the
    // display: only `no` reveals the linked device. `yes` and `na`
    // display "Not applicable". Schemas without the switch (Modules)
    // always show the linked device or "—" / "No date" fallbacks.
    const _hasTristate = fields.some((x) => x.key === 'acceptsModule');
    const _moduleState = (() => {
      let s = metadata.acceptsModule;
      if (s === true)  s = 'yes';
      if (s === false) s = 'no';
      return s;
    })();
    const _hideDevice = _hasTristate && _moduleState !== 'no';

    if (f.kind === 'utDeviceName') {
      const dev = equipment.utDevice;
      const display = _hideDevice ? 'Not applicable' : (dev?.name || '—');
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <div className="equipment-readonly">{display}</div>
        </div>
      );
    }

    if (f.kind === 'utDeviceExpiration') {
      const dev = equipment.utDevice;
      if (_hideDevice) {
        return (
          <div className="form-group" key={f.key}>
            <label>{f.label}</label>
            <div className="equipment-readonly">Not applicable</div>
          </div>
        );
      }
      const iso = dev?.dateEndCertificateValidity || null;
      const cls = expiryClass(iso);
      const icon = expiryIcon(cls);
      const display = formatDateBadge(iso);
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <span className={`ddl-expiry-badge ${cls}`}>
            {display ? <>{icon}{display}</> : <span className="ddl-expiry-placeholder">No date</span>}
          </span>
        </div>
      );
    }

    // Probe Modules: read-only Probe name from the linked payload
    // (equipment.probe.name). No expiration tracked.
    if (f.kind === 'probeName') {
      const display = equipment.probe?.name || '—';
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <div className="equipment-readonly">{display}</div>
        </div>
      );
    }

    // ── Number-with-suffix (Angle / Frequency on Probe Modules) ───
    // Stored normalised ("45°" / "5 MHz"); displayed without the suffix
    // so the user can edit the number freely. On blur, empty resets to
    // the default value and any bare number gets the suffix re-attached.
    if (f.kind === 'numberWithSuffix') {
      const stored = String(metadata[f.key] ?? '');
      const display = stored.endsWith(f.suffix) ? stored.slice(0, -f.suffix.length) : stored;
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="text"
              inputMode="decimal"
              className="equipment-detail-input"
              value={display}
              onChange={(e) => {
                // Allow only digits, dot, comma, minus during editing.
                const cleaned = e.target.value.replace(/[^\d.,-]/g, '');
                setField(f.key, cleaned);
              }}
              onBlur={(e) => {
                const v = String(e.target.value).trim();
                if (!v) setField(f.key, f.defaultValue);
                else setField(f.key, `${v}${f.suffix}`);
              }}
              placeholder={f.defaultValue}
              style={{ flex: 1 }}
            />
            <span style={{ opacity: 0.65, minWidth: '2.5rem' }}>{f.suffix.trim()}</span>
          </div>
        </div>
      );
    }

    // ── Auto-default text (Primary gain / Range on Probe Modules) ──
    // Free-text input; empty value on blur reverts to the default
    // ("Auto") so the PDF always has something to render.
    if (f.kind === 'autoText') {
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <input
            type="text"
            className="equipment-detail-input"
            value={String(metadata[f.key] ?? '')}
            onChange={(e) => setField(f.key, e.target.value)}
            onBlur={(e) => {
              if (!String(e.target.value).trim()) setField(f.key, f.defaultValue);
            }}
            placeholder={f.defaultValue}
          />
        </div>
      );
    }

    if (f.kind === 'select') {
      const opts = getDDLLabels(ddlCollections, f.dropdownId);
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <select
            className="equipment-detail-input"
            value={v}
            onChange={(e) => setField(f.key, e.target.value)}
          >
            <option value="">— Select —</option>
            {opts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {opts.length === 0 && (
            <div className="equipment-detail-hint">
              No DDL list assigned to <code>{f.dropdownId}</code>.
            </div>
          )}
        </div>
      );
    }

    if (f.kind === 'iso') {
      const isDefault = (v || '').trim() === f.defaultValue || !(v || '').trim();
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          {editingIso ? (
            <input
              className="equipment-detail-input"
              autoFocus
              value={v}
              onChange={(e) => setField(f.key, e.target.value)}
              onBlur={() => {
                // Empty → revert to default.
                if (!(metadata[f.key] || '').trim()) setField(f.key, f.defaultValue);
                setEditingIso(false);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
            />
          ) : (
            <div
              className={`equipment-iso-display${isDefault ? ' is-default' : ''}`}
              onDoubleClick={() => {
                // Clear the field so the user starts from a blank input.
                setField(f.key, '');
                setEditingIso(true);
              }}
              title="Double-click to edit"
            >
              {v || f.defaultValue}
            </div>
          )}
          <div className="equipment-detail-hint">
            Double-click to edit. Leave empty to revert to <em>{f.defaultValue}</em>.
          </div>
        </div>
      );
    }

    if (f.kind === 'format') {
      return (
        <div className="form-group" key={f.key}>
          <label>{f.label}</label>
          <input
            className="equipment-detail-input"
            value={v}
            onChange={(e) => setField(f.key, e.target.value)}
            onBlur={(e) => setField(f.key, normaliseFormat(e.target.value))}
            placeholder=".JPG, .MP4, …"
          />
          <div className="equipment-detail-hint">
            Saved uppercase with a leading dot. "Not applicable" / "Not available" are kept as-is.
          </div>
        </div>
      );
    }

    // Plain text
    return (
      <div className="form-group" key={f.key}>
        <label>{f.label}</label>
        <input
          className="equipment-detail-input"
          value={v}
          onChange={(e) => setField(f.key, e.target.value)}
        />
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal equipment-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2 className="equipment-detail-heading">
              <span>{equipment.name || 'Equipment'}</span>
              {equipment.id && (
                <span className="equipment-detail-id" title="Equipment ID">{equipment.id}</span>
              )}
            </h2>
            <div className="equipment-detail-sub">
              {[equipment.manufacturer, category].filter(Boolean).join(' · ')}
              {equipment.deleted && (
                <span className="equipment-detail-deleted-hint">
                  {' '}· This equipment no longer exists in 3DInspect.
                </span>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        {loading ? (
          <div className="equipment-detail-loading">Loading…</div>
        ) : (
          <>
            <div className="equipment-detail-form">
              {fields.map(renderField)}
            </div>

            {error && <div className="equipment-error">{error}</div>}

            <div className="equipment-detail-footer">
              {equipment.deleted && (
                <button
                  className="btn btn-danger equipment-detail-delete"
                  onClick={handleDelete}
                  disabled={saving}
                  title="Delete saved metadata for this orphaned equipment."
                >
                  Delete
                </button>
              )}
              <div className="equipment-detail-footer-spacer" />
              <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
